import React from 'react';
import { Link } from 'react-router-dom';
import { Instagram, Loader } from 'lucide-react';
import useRegionStore from '@/context/RegionStore';
import useCountryDetailsStore from '@/context/CountryDetailsStore';
import { buildRegionUrl } from '@/utils/regionDetection';
import { getRegionalHomePath, getRegionalProductPath, SEO_REGIONS } from '@/utils/seoUrls';

interface FooterProps {
    className?: string;
    style?: React.CSSProperties;
    isRegionModalOpen?: boolean;
    onRegionModalOpen?: () => void;
}

const Footer: React.FC<FooterProps> = ({ className = '', style = {}, isRegionModalOpen = false, onRegionModalOpen }) => {
    const { chosenRegion, detectedRegion, isDetectingRegion } = useRegionStore();
    const { getRegionInfo } = useCountryDetailsStore();
    const region = chosenRegion || detectedRegion || 'gb';

    const countryInfo = getRegionInfo(chosenRegion || detectedRegion);

    return (
        <footer className={`bg-[#353535] text-white pt-12 rounded-t-[32px] ${className}`} style={style}>
            <div className="standard_container mx-auto px-6" style={{ flexDirection: 'column' }}>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                    {/* Company Info */}
                    <div className="flex flex-col gap-4">
                        <Link to="/" className="text-white hover:text-white/30 transition-colors">
                            <h3 className="text-xl font-headline">Kibu Shop</h3>
                        </Link>
                        <p className="text-gray-300">
                            Creating the best headphones for kids, designed with safety and style in mind.
                        </p>

                        {/* Region Selector Button */}
                        <button
                            onClick={onRegionModalOpen}
                            disabled={isDetectingRegion}
                            className="flex items-center gap-2 text-gray-300 hover:text-white transition-colors bg-[#454545] hover:bg-[#555555] rounded-lg px-4 py-2 mt-4 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {isDetectingRegion ? (
                                <Loader className="w-4 h-4 animate-spin" />
                            ) : (
                                <span className="text-lg">{countryInfo?.flag || '🌍'}</span>
                            )}
                            <span className="flex-1 text-left text-sm">
                                {countryInfo?.name || (chosenRegion?.toUpperCase() || 'Select Region')}
                            </span>
                        </button>
                    </div>

                    {/* Quick Links */}
                    <div className="flex flex-col gap-4">
                        <h3 className="text-xl font-headline">Explore</h3>
                        <div className="flex flex-col gap-2">
                            <Link to={getRegionalProductPath(region)} className="text-gray-300 hover:text-white transition-colors">
                                Shop Headphones
                            </Link>
                            <Link to="/build" className="text-gray-300 hover:text-white transition-colors">
                                Build Instructions
                            </Link>
                            <Link to="/blog/" className="text-gray-300 hover:text-white transition-colors">
                                Journal
                            </Link>
                            <Link to="/sustainability/" className="text-gray-300 hover:text-white transition-colors">
                                Sustainability
                            </Link>
                            <Link to={buildRegionUrl(region, 'repair/parts')} className="text-gray-300 hover:text-white transition-colors">
                                Repair &amp; Spare Parts
                            </Link>
                        </div>
                    </div>

                    <div className="flex flex-col gap-4">
                        <Link to="/legal/" className="text-white hover:text-white/30 transition-colors">
                            <h3 className="text-xl font-headline">Legal</h3>
                        </Link>
                        <div className="flex flex-col gap-2">
                            <Link to='/legal/' className="text-gray-300 hover:text-white transition-colors">
                                All Legal Documents
                            </Link>
                            <Link to='/legal/terms-of-service/' className="text-gray-300 hover:text-white transition-colors">
                                Terms of Service
                            </Link>
                            <Link to='/legal/kibu-care-terms/' className="text-gray-300 hover:text-white transition-colors">
                                Kibu Care Terms
                            </Link>
                            <Link to='/legal/privacy-policy/' className="text-gray-300 hover:text-white transition-colors">
                                Privacy Policy
                            </Link>
                            <Link to='/legal/delivery-and-returns/' className="text-gray-300 hover:text-white transition-colors">
                                Delivery, Returns & Recycling
                            </Link>
                        </div>
                    </div>

                    {/* Social Links */}
                    <div className="flex flex-col gap-4">
                        <h3 className="text-xl font-headline">Connect With Us</h3>
                        <div className="flex gap-4">
                            <a href="https://www.instagram.com/kibu.family/" target="_blank" rel="noopener noreferrer"
                                aria-label="Visit our Instagram"
                                className="text-gray-300 hover:text-white transition-colors">
                                <Instagram size={24} />
                            </a>
                        </div>
                    </div>
                </div>

                <nav className="mt-8 border-t border-white/20 pt-6" aria-label="Regional stores">
                    <h3 className="text-sm font-headline text-gray-300 mb-3">Shop by region</h3>
                    <div className="flex flex-wrap gap-x-5 gap-y-2">
                        {SEO_REGIONS.map(({ code, label, hreflang }) => (
                            <Link
                                key={code}
                                to={getRegionalHomePath(code)}
                                hrefLang={hreflang}
                                className="text-sm text-gray-300 hover:text-white transition-colors"
                            >
                                {label}
                            </Link>
                        ))}
                    </div>
                </nav>


            </div>
            <div className="mt-12 py-2 text-center text-gray-400 text-sm">
                <p>&copy; {new Date().getFullYear()} Kibu Limited. All rights reserved.</p>
                <p>VAT registration: GB 530 3078 26</p>
            </div>
        </footer>
    );
};

export default Footer; 
